import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortfolioPage = () => {
  const [albums, setAlbums] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/albums`);
        setAlbums(response.data);
      } catch (error) {
        console.error('Error fetching albums:', error);
        toast({
          title: 'Error',
          description: 'Failed to load albums.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, [toast]);

  // After albums load, if URL has a hash like #album-<id>, scroll the album into view
  useEffect(() => {
    if (loading || albums.length === 0) return;
    if (!location.hash || !location.hash.startsWith('#album-')) return;
    const id = location.hash.replace('#album-', '');
    // Wait a tick for DOM nodes to mount
    const t = setTimeout(() => {
      const el = document.getElementById(`album-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-blue-500');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500'), 2000);
      }
    }, 100);
    return () => clearTimeout(t);
  }, [loading, albums, location.hash]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Portfolio</h1>
            <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Showcasing teaching moments and school activities</p>
          </div>

          {albums.length > 0 ? (
            <div className="space-y-12">
              {albums.map((album) => (
                <Card
                  key={album._id}
                  id={`album-${album._id}`}
                  className="border-blue-100 overflow-hidden scroll-mt-24"
                >
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-900">{album.title}</CardTitle>
                    <CardDescription className="text-base">{album.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {album.photos && album.photos.length > 0 ? (
                      <div className="relative">
                        <div className="overflow-hidden rounded-lg">
                          <div 
                            className="flex transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(-${currentAlbumIndex * 100}%)` }}
                          >
                            {album.photos.map((photo, photoIdx) => (
                              <div
                                key={photoIdx}
                                className="min-w-full cursor-pointer"
                                onClick={() => photo.type !== 'video' && setSelectedPhoto({ album, photo, index: photoIdx })}
                              >
                                {photo.type === 'video' ? (
                                  <video
                                    src={photo.url}
                                    controls
                                    className="w-full h-96 object-contain bg-black rounded"
                                  />
                                ) : (
                                  <img
                                    src={photo.url}
                                    alt={photo.caption || album.title}
                                    className="w-full h-96 object-contain bg-gray-100"
                                  />
                                )}
                                {photo.caption && (
                                  <p className="text-center text-gray-600 mt-4 italic">{photo.caption}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {album.photos.length > 1 && (
                          <>
                            <button
                              onClick={() => setCurrentAlbumIndex((prev) => (prev === 0 ? album.photos.length - 1 : prev - 1))}
                              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                              aria-label="Previous photo"
                            >
                              <ChevronLeft className="w-6 h-6 text-gray-800" />
                            </button>
                            <button
                              onClick={() => setCurrentAlbumIndex((prev) => (prev === album.photos.length - 1 ? 0 : prev + 1))}
                              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                              aria-label="Next photo"
                            >
                              <ChevronRight className="w-6 h-6 text-gray-800" />
                            </button>

                            <div className="flex justify-center gap-2 mt-6">
                              {album.photos.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setCurrentAlbumIndex(idx)}
                                  className={`w-3 h-3 rounded-full transition-all ${
                                    idx === currentAlbumIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                                  }`}
                                  aria-label={`Go to photo ${idx + 1}`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">No photos yet</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No albums available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedPhoto && selectedPhoto.photo.type !== 'video' && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-5xl max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.photo.url}
              alt={selectedPhoto.photo.caption || selectedPhoto.album.title}
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
          {selectedPhoto.photo.caption && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 text-white px-6 py-3 rounded-lg max-w-2xl text-center">
              {selectedPhoto.photo.caption}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PortfolioPage;
